/*
 * Interactive sample picker used by snippets/cart-upsell-samples.liquid.
 *
 * Defined once (loaded site-wide from layout/theme.liquid). Because it is a
 * custom element, the browser upgrades and runs connectedCallback() every time
 * the cart drawer re-renders its markup via innerHTML — so the picker keeps
 * working across AJAX cart refreshes without re-running any inline script.
 */
if (!customElements.get('cart-upsell-samples')) {
  customElements.define(
    'cart-upsell-samples',
    class CartUpsellSamples extends HTMLElement {
      connectedCallback() {
        if (this._initialized) return;
        this._initialized = true;

        this.form = this.querySelector('[data-sample-bundle-form]');
        if (!this.form) return;

        this.selects = Array.from(this.form.querySelectorAll('[data-sample-slot]'));
        this.submitButton = this.form.querySelector('.cart-upsell-form__submit');
        this.status = this.form.querySelector('.cart-upsell-form__status');

        this.selects.forEach((dropdown) => {
          const btn = dropdown.querySelector('.cart-upsell-custom-select__btn');
          const list = dropdown.querySelector('.cart-upsell-custom-select__list');
          const opts = Array.from(dropdown.querySelectorAll('.cart-upsell-custom-select__opt'));

          btn.addEventListener('click', (e) => {
            e.stopPropagation();
            const isOpen = !list.hidden;
            this._closeAll();
            if (!isOpen) {
              list.hidden = false;
              btn.setAttribute('aria-expanded', 'true');
            }
          });

          opts.forEach((opt) => {
            opt.addEventListener('click', () => {
              dropdown.dataset.value = opt.dataset.value;
              opts.forEach((o) => o.setAttribute('aria-selected', 'false'));
              opt.setAttribute('aria-selected', 'true');

              const label = btn.querySelector('.cart-upsell-custom-select__current');
              if (opt.dataset.value) {
                label.textContent =
                  opt.querySelector('.cart-upsell-custom-select__opt-name')?.textContent || '';
                label.classList.add('has-value');
              } else {
                label.textContent = 'Select fragrance...';
                label.classList.remove('has-value');
              }

              list.hidden = true;
              btn.setAttribute('aria-expanded', 'false');

              this.updatePreview(dropdown);
              this.validate();
            });
          });
        });

        document.addEventListener('click', () => this._closeAll());

        this.form.addEventListener('submit', this.onSubmit.bind(this));
        this.validate();
      }

      _closeAll() {
        this.selects.forEach((d) => {
          const list = d.querySelector('.cart-upsell-custom-select__list');
          const btn = d.querySelector('.cart-upsell-custom-select__btn');
          if (list) list.hidden = true;
          if (btn) btn.setAttribute('aria-expanded', 'false');
        });
      }

      updatePreview(dropdown) {
        const slot = dropdown.dataset.sampleSlot;
        const preview = this.querySelector(`[data-sample-preview-${slot}]`);
        if (!preview) return;

        const img = preview.querySelector('.cart-upsell-slot__img');
        const placeholder = preview.querySelector('.cart-upsell-slot__placeholder');
        const value = dropdown.dataset.value;
        const selectedOpt = value
          ? dropdown.querySelector(`.cart-upsell-custom-select__opt[data-value="${value}"]`)
          : null;

        if (value && selectedOpt && selectedOpt.dataset.image) {
          img.src = selectedOpt.dataset.image;
          img.alt = selectedOpt.dataset.name || '';
          img.hidden = false;
          if (placeholder) placeholder.hidden = true;
        } else {
          img.removeAttribute('src');
          img.alt = '';
          img.hidden = true;
          if (placeholder) placeholder.hidden = false;
        }
      }

      validate() {
        const allChosen =
          this.selects.length > 0 && this.selects.every((s) => s.dataset.value !== '');
        this.submitButton.disabled = !allChosen;
      }

      setStatus(message, state) {
        if (!this.status) return;
        this.status.textContent = message || '';
        if (state) {
          this.status.dataset.state = state;
        } else {
          delete this.status.dataset.state;
        }
      }

      async onSubmit(event) {
        event.preventDefault();
        if (this.submitButton.disabled) return;

        const items = this.selects.map((dropdown) => ({
          id: Number(dropdown.dataset.value),
          quantity: 1,
        }));

        this.submitButton.disabled = true;
        this.setStatus('Adding...', null);

        const cart =
          document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        const sections = cart ? cart.getSectionsToRender().map((section) => section.id) : [];

        try {
          const config = {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/javascript',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({
              items,
              sections,
              sections_url: window.location.pathname,
            }),
          };

          const response = await fetch(`${routes.cart_add_url}.js`, config);
          const data = await response.json();

          if (!response.ok || data.status) {
            throw new Error(data.description || data.message || 'Failed to add sample');
          }

          this.setStatus('Added!', 'success');
          this.resetForm();

          if (cart) {
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'cart-upsell-samples',
                cartData: data,
              });
            }
            cart.renderContents(data);
            cart.classList.remove('is-empty');
          } else {
            window.location.href = (typeof routes !== 'undefined' && routes.cart_url) || '/cart';
          }
        } catch (error) {
          console.error(error);
          this.setStatus(error.message || 'Something went wrong. Please try again.', 'error');
          this.submitButton.disabled = false;
        }
      }

      resetForm() {
        this.selects.forEach((dropdown) => {
          dropdown.dataset.value = '';
          dropdown
            .querySelectorAll('.cart-upsell-custom-select__opt')
            .forEach((o) => o.setAttribute('aria-selected', 'false'));
          const btn = dropdown.querySelector('.cart-upsell-custom-select__btn');
          const label = btn.querySelector('.cart-upsell-custom-select__current');
          label.textContent = 'Select fragrance...';
          label.classList.remove('has-value');
          btn.setAttribute('aria-expanded', 'false');
          dropdown.querySelector('.cart-upsell-custom-select__list').hidden = true;
          this.updatePreview(dropdown);
        });
        this.validate();
      }
    }
  );
}
